import React, { useState } from 'react';
import MicrositeLayout from '../MicrositeLayout.jsx';
import {
  getSocialUrl,
  extractYouTubeId,
  isYouTubeShort,
  linkIconMap,
  tipoEventoLabels,
  getFontById,
  isMicrositeSectionHidden,
  MICROSITE_SECTION_EMPTY_HINT,
  getMicrositeSectionLabel,
  getMicrositeHeroImageUrl,
  getCabeceraHeroVisibilidad,
  normalizeContactoMostrar,
  isMicrositeContactoSectionVisuallyEmpty,
  getMicrositeBodySectionOrder,
  micrositeCalendarioActuacionesVisibles,
  micrositeCalendarioEnsayosVisibles } from './micrositeUtils';
import { ScrollReveal,
  ScrollRevealSoft } from './MicrositeScrollReveal.jsx';
import MicrositePlantillaMusicos from './MicrositePlantillaMusicos.jsx';
import MicrositeLogrosBlock from './MicrositeLogrosBlock.jsx';
import MicrositeAvalesBlock from './MicrositeAvalesBlock.jsx';
import MicrositeResenasBlock from './MicrositeResenasBlock.jsx';
import MicrositeNoticiasBlock from './MicrositeNoticiasBlock.jsx';
import MicrositeCalendario from './MicrositeCalendario.jsx';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Image,
  Icon,
  SimpleGrid,
  Flex,
  Wrap,
  WrapItem,
  Link,
  Button,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Divider } from '@/components/ui/chakra-v2-compat';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaMapMarkerAlt, FaUsers, FaCalendarAlt, FaCheckCircle, FaStar, FaRegStar,
  FaMusic, FaYoutube, FaClock, FaChurch, FaInstagram, FaFacebook,
  FaEnvelope, FaPhone, FaQuoteLeft,
  FaGlobe, FaLink,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const ElegantHeading = ({ children, fontFamily }) => (
  <VStack gap={4} mb={{ base: 8, md: 12 }}>
    <Heading
      size={{ base: 'md', md: 'lg' }}
      fontWeight="400" textAlign="center"
      textTransform="uppercase" letterSpacing="0.2em" color="gray.700"
      fontFamily={fontFamily || undefined}
    >
      {children}
    </Heading>
    <Box w="40px" h="1px" bg="gray.300" />
  </VStack>
);

const ElegantSection = ({ id, children, py, noBorder, sx }) => (
  <Box as="section" id={id} bg="white" py={py || { base: 14, md: 18 }} scrollMarginTop="56px"
    borderBottom={noBorder ? 'none' : '1px solid'} borderColor="gray.200" sx={sx}>
    {children}
  </Box>
);

const MicrositeElegante = ({
  bandaData, avales, stats, sections, msConfig, contacto, social, ensayosPublicos, eventosPublicos, actuacionesPublicas, locationText, hasSocials,
  primaryColor, surfaceColor = '#f7fafc',
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAllRepertoire, setShowAllRepertoire] = useState(false);
  const repertorioVisible = showAllRepertoire ? bandaData.repertorio : (bandaData.repertorio || []).slice(0, 15);
  const headingFont = getFontById(msConfig.tipografia).family || undefined;
  const secHidden = (id) => isMicrositeSectionHidden(id, msConfig);
  const enlacesItems = msConfig.enlacesPersonalizados || [];
  const heroImageUrl = getMicrositeHeroImageUrl(bandaData);
  const hv = getCabeceraHeroVisibilidad(msConfig);
  const heroHasSocials = hv.mostrarRrss && hasSocials;
  const musicosPlantilla = Array.isArray(bandaData.musicos) ? bandaData.musicos : [];
  const showPlantillaMusicos = msConfig.mostrarPlantillaMusicosNosotros === true && musicosPlantilla.length > 0;
  const showEspecialidadesWeb = msConfig.mostrarEspecialidadesNosotros !== false;
  const hasNosotrosContent =
    Boolean(bandaData.bio) ||
    (showEspecialidadesWeb && bandaData.especialidades?.length > 0) ||
    showPlantillaMusicos;
  const cm = normalizeContactoMostrar(msConfig.contactoMostrar);
  const contactoBlockEmpty = isMicrositeContactoSectionVisuallyEmpty(
    contacto,
    locationText,
    hasSocials,
    msConfig.textoContratar,
    msConfig,
  );

  const bodySectionOrder = getMicrositeBodySectionOrder(msConfig);
  const secSx = (id) => ({ order: bodySectionOrder.indexOf(id) });
  const calActsVis = micrositeCalendarioActuacionesVisibles(actuacionesPublicas, msConfig);
  const calEnsVis = micrositeCalendarioEnsayosVisibles(ensayosPublicos, msConfig);

  return (
    <MicrositeLayout bandaData={bandaData} micrositeConfig={msConfig} sections={sections} tema="elegante">

      {/* HERO SPLIT */}
      <Flex id="inicio" minH={{ base: '80vh', md: '90vh' }} direction={{ base: 'column', lg: 'row' }} scrollMarginTop="56px">
        <Box flex={1} position="relative" minH={{ base: '45vh', lg: 'auto' }}>
          {heroImageUrl ? (
            <Image
              src={heroImageUrl}
              alt={bandaData.nombre} w="full" h="full" objectFit="cover" position="absolute" inset={0}
            />
          ) : (
            <Box position="absolute" inset={0} bg="gray.800" />
          )}
        </Box>
        <Flex flex={1} bg={primaryColor} align="center" justify="center" p={{ base: 8, md: 12, lg: 16 }}>
          <VStack gap={6} textAlign={{ base: 'center', lg: 'left' }} align={{ base: 'center', lg: 'flex-start' }} maxW="lg">
            <VStack gap={2} align={{ base: 'center', lg: 'flex-start' }}>
              <ScrollReveal>
                <HStack gap={2}>
                  <Heading size={{ base: 'xl', md: '2xl' }} color="white" fontWeight="300" letterSpacing="0.05em" lineHeight="1.2" fontFamily={headingFont}>
                    {bandaData.nombre}
                  </Heading>
                  {hv.mostrarVerificado && bandaData.verificationStatus === 'VERIFIED' && <Tooltip content="Perfil verificado" showArrow><span><Icon color="blue.300" w={5} h={5} asChild><FaCheckCircle /></Icon></span></Tooltip>}
                </HStack>
              </ScrollReveal>
              {hv.mostrarUbicacion && locationText && (
                <ScrollRevealSoft delay={0.06}>
                  <HStack gap={2} color="gray.400"><Icon boxSize={3} asChild><FaMapMarkerAlt /></Icon><Text fontSize="sm" fontWeight="400" letterSpacing="0.05em">{locationText}</Text></HStack>
                </ScrollRevealSoft>
              )}
            </VStack>

            <ScrollRevealSoft delay={0.12}>
              <HStack gap={6} color="gray.500" fontSize="xs" letterSpacing="0.1em" textTransform="uppercase" flexWrap="wrap" justify={{ base: 'center', lg: 'flex-start' }}>
                {hv.mostrarNMusicos && bandaData.nMusicos && <HStack gap={1}><Icon boxSize={3} asChild><FaUsers /></Icon><Text>{bandaData.nMusicos} musicos</Text></HStack>}
                {hv.mostrarAnoFundacion && bandaData.anoFundacion && <HStack gap={1}><Icon boxSize={3} asChild><FaCalendarAlt /></Icon><Text>Desde {bandaData.anoFundacion}</Text></HStack>}
                {hv.mostrarTipoBanda && bandaData.tipoGrupo && <HStack gap={1}><Icon boxSize={3} asChild><FaMusic /></Icon><Text>{bandaData.tipoGrupo}</Text></HStack>}
              </HStack>
            </ScrollRevealSoft>

            {heroHasSocials && (
              <ScrollRevealSoft delay={0.18}>
                <HStack gap={5}>
                  {social.instagram && <Link
                    href={getSocialUrl('instagram', social.instagram)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={4}
                    color="gray.500"
                    _hover={{ color: 'white' }}
                    transition="color 0.3s"
                    asChild><FaInstagram /></Icon></Link>}
                  {social.facebook && <Link
                    href={getSocialUrl('facebook', social.facebook)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={4}
                    color="gray.500"
                    _hover={{ color: 'white' }}
                    transition="color 0.3s"
                    asChild><FaFacebook /></Icon></Link>}
                  {social.youtube && <Link
                    href={getSocialUrl('youtube', social.youtube)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={4}
                    color="gray.500"
                    _hover={{ color: 'white' }}
                    transition="color 0.3s"
                    asChild><FaYoutube /></Icon></Link>}
{social.twitter && <Link
                    href={getSocialUrl('twitter', social.twitter)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={4}
                    color="gray.500"
                    _hover={{ color: 'white' }}
                    transition="color 0.3s"
                    asChild><FaXTwitter /></Icon></Link>}
                </HStack>
              </ScrollRevealSoft>
            )}

            <ScrollRevealSoft delay={heroHasSocials ? 0.24 : 0.18}>
              <Button variant="outline" color="white" borderColor="gray.600" rounded="none" px={8} py={5} fontSize="xs" letterSpacing="0.15em" textTransform="uppercase" fontWeight="400"
                _hover={{ bg: 'white', color: 'gray.900' }} transition="all 0.3s"
                onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}>
                {msConfig.textoContratar ? 'Contactar' : 'Contactanos'}
              </Button>
            </ScrollRevealSoft>
          </VStack>
        </Flex>
      </Flex>

      <Flex direction="column" w="full">
      {/* SOBRE NOSOTROS */}
      {!secHidden('nosotros') && (
        <ElegantSection id="nosotros" sx={secSx('nosotros')}>
          <Container maxW="3xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('nosotros')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {hasNosotrosContent ? (
                <>
                  {bandaData.bio && (
                    <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="2" color="gray.600" textAlign="center" whiteSpace="pre-line" fontWeight="300">{bandaData.bio}</Text>
                  )}
                  {showEspecialidadesWeb && bandaData.especialidades?.length > 0 && (
                    <Wrap gap={3} justify="center" mt={bandaData.bio ? 8 : 0}>
                      {bandaData.especialidades.map((esp, i) => <WrapItem key={i}><Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.1em" px={3} py={1} border="1px solid" borderColor="gray.200">{esp}</Text></WrapItem>)}
                    </Wrap>
                  )}
                  {showPlantillaMusicos && (
                    <MicrositePlantillaMusicos musicos={musicosPlantilla} micrositeConfig={msConfig} primaryColor={primaryColor} variant="elegante" />
                  )}
                </>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center" fontWeight="300">{MICROSITE_SECTION_EMPTY_HINT.nosotros}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* HISTORIA */}
      {!secHidden('historia') && (
        <ElegantSection id="historia" sx={secSx('historia')}>
          <Container maxW="3xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('historia')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {msConfig.historia ? (
                <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="2" color="gray.600" textAlign="center" whiteSpace="pre-line" fontWeight="300">{msConfig.historia}</Text>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.historia}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* NOTICIAS */}
      {!secHidden('noticias') && (
        <ElegantSection id="noticias" sx={secSx('noticias')}>
          <Container maxW="4xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('noticias')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              <MicrositeNoticiasBlock msConfig={msConfig} primaryColor={primaryColor} />
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* DIRECTIVA */}
      {!secHidden('directiva') && (
        <ElegantSection id="directiva" sx={secSx('directiva')}>
          <Container maxW="4xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('directiva')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.directiva?.length > 0) ? (
                <Flex flexWrap="wrap" justify="center" align="flex-start" gap={{ base: 6, md: 8 }} w="full">
                  {msConfig.directiva.map((m, i) => (
                    <VStack key={m.id || i} gap={3} textAlign="center" align="center" minW="140px" maxW="220px" flex="0 0 auto">
                      <Avatar.Root size="md" bg="gray.100" color="gray.500"><Avatar.Fallback name={m.nombre} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
                      <VStack gap={0}>
                        <Text fontWeight="500" fontSize="sm" color="gray.800" letterSpacing="0.02em">{m.nombre}</Text>
                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.08em" fontWeight="400">{m.cargo}</Text>
                      </VStack>
                    </VStack>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.directiva}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* GALERIA */}
      {!secHidden('galeria') && (
        <ElegantSection id="galeria" sx={secSx('galeria')}>
          <Container maxW="5xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>Galeria</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.fotos?.length > 0 ? (
                <Flex flexWrap="wrap" justifyContent="center" alignItems="flex-start" gap={4} rowGap={5} w="full">
                  {bandaData.fotos.map((foto, i) => (
                    <Box key={i} flex={{ base: '1 1 100%', sm: '0 1 calc(50% - 8px)', lg: '0 1 calc(33.33% - 11px)' }} maxW={{ base: '100%', sm: '420px', lg: '360px' }} minW={0} overflow="hidden" cursor="pointer" onClick={() => setSelectedImage(foto)} role="group" rounded="md">
                      <Image src={foto} alt={`Foto ${i + 1}`} w="full" objectFit="cover" transition="all 0.4s ease"
                        _groupHover={{ transform: 'scale(1.02)', opacity: 0.9 }} />
                    </Box>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.galeria}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* VIDEOS */}
      {!secHidden('videos') && (
        <ElegantSection id="videos" sx={secSx('videos')}>
          <Container maxW="4xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('videos')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.videos?.length > 0 ? (() => {
                const normalVideos = bandaData.videos.filter((v) => !isYouTubeShort(v.url));
                const shorts = bandaData.videos.filter((v) => isYouTubeShort(v.url));
                const iframeAllow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
                return (
                  <VStack gap={10} align="center" w="full">
                    {normalVideos.length > 0 && (
                      <Box w="full">
                        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={4} textTransform="uppercase" letterSpacing="0.1em" textAlign="center" w="full">
                          Vídeos
                        </Text>
                        <Flex flexWrap="wrap" justifyContent="center" gap={8} w="full">
                          {normalVideos.map((video, i) => {
                            const videoId = extractYouTubeId(video.url);
                            return (
                              <Box key={video.id ?? `v-n-${i}`} flex={{ base: '1 1 100%', md: '0 1 800px' }} maxW={{ base: '100%', md: '800px' }} minW={0} w="full">
                                {videoId ? (
                                  <Box position="relative" pb="56.25%" w="full">
                                    <iframe src={`https://www.youtube.com/embed/${videoId}`} title={video.titulo} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow={iframeAllow} allowFullScreen />
                                  </Box>
                                ) : (
                                  <Box h="200px" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
                                    <Icon w={12} h={12} color="gray.300" asChild><FaYoutube /></Icon>
                                  </Box>
                                )}
                                {video.titulo && (
                                  <Text fontSize="sm" color="gray.500" mt={3} textAlign="center" fontWeight="400" letterSpacing="0.02em">
                                    {video.titulo}
                                  </Text>
                                )}
                              </Box>
                            );
                          })}
                        </Flex>
                      </Box>
                    )}
                    {shorts.length > 0 && (
                      <Box w="full">
                        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={4} textTransform="uppercase" letterSpacing="0.1em" textAlign="center" w="full">
                          Shorts
                        </Text>
                        <Flex flexWrap="wrap" justifyContent="center" alignItems="flex-start" gap={{ base: 3, md: 4 }} rowGap={5} w="full">
                          {shorts.map((video, i) => {
                            const videoId = extractYouTubeId(video.url);
                            return (
                              <Box key={video.id ?? `v-s-${i}`} flex="0 0 auto" w={{ base: 'calc(50% - 6px)', sm: '200px', md: '240px' }} maxW="260px" display="flex" flexDirection="column" alignItems="center">
                                <Box maxW="260px" w="full">
                                  {videoId ? (
                                    <Box position="relative" pb="177.78%" w="full" rounded="md" overflow="hidden">
                                      <iframe src={`https://www.youtube.com/embed/${videoId}`} title={video.titulo} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow={iframeAllow} allowFullScreen />
                                    </Box>
                                  ) : (
                                    <Box h="120px" bg="gray.50" display="flex" alignItems="center" justifyContent="center" rounded="md">
                                      <Icon w={8} h={8} color="gray.300" asChild><FaYoutube /></Icon>
                                    </Box>
                                  )}
                                </Box>
                                {video.titulo && (
                                  <Text fontSize="xs" color="gray.500" mt={2} textAlign="center" fontWeight="400" lineClamp={2} lineHeight="short">
                                    {video.titulo}
                                  </Text>
                                )}
                              </Box>
                            );
                          })}
                        </Flex>
                      </Box>
                    )}
                  </VStack>
                );
              })() : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.videos}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* REPERTORIO */}
      {!secHidden('repertorio') && (
        <ElegantSection id="repertorio" sx={secSx('repertorio')}>
          <Container maxW="3xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('repertorio')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.repertorio?.length > 0 ? (
                <>
                  <Text textAlign="center" color="gray.400" fontSize="xs" mb={6} textTransform="uppercase" letterSpacing="0.1em">{bandaData.repertorio.length} obras</Text>
                  <VStack gap={0} align="stretch">
                    {repertorioVisible.map((item, i) => {
                      const nombre = item.nombre || item.marcha?.nombre || '';
                      const autor = item.autor || item.marcha?.autor || '';
                      const tipo = item.tipo || item.marcha?.tipo || '';
                      return (
                        <Flex key={item.id || i} py={3} align="center" borderBottom="1px solid" borderColor="gray.100">
                          <Text fontSize="xs" fontWeight="400" color="gray.300" minW="30px">{String(i + 1).padStart(2, '0')}</Text>
                          <VStack align="start" gap={0} flex={1}>
                            <Text fontWeight="500" fontSize="sm" color="gray.800" letterSpacing="0.01em">{nombre}</Text>
                            <HStack gap={2}>{autor && <Text fontSize="xs" color="gray.400" fontWeight="300">{autor}</Text>}{tipo && <Text fontSize="xs" color="gray.300">— {tipo}</Text>}</HStack>
                          </VStack>
                        </Flex>
                      );
                    })}
                  </VStack>
                  {bandaData.repertorio.length > 15 && !showAllRepertoire && (
                    <Flex justify="center" mt={6}><Button variant="outline" size="sm" rounded="none" borderColor="gray.300" color="gray.600" fontSize="xs" letterSpacing="0.1em" textTransform="uppercase" onClick={() => setShowAllRepertoire(true)}>Ver todo ({bandaData.repertorio.length})</Button></Flex>
                  )}
                </>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.repertorio}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* LOGROS */}
      {!secHidden('logros') && (
        <ElegantSection id="logros" sx={secSx('logros')}>
          <Container maxW="5xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('logros')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.logros?.length > 0) ? (
                <MicrositeLogrosBlock logros={msConfig.logros} primaryColor={primaryColor} surfaceColor={surfaceColor} variant="elegante" />
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.logros}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* AVALES Y RESENAS */}
      {!secHidden('resenas') && (
        <ElegantSection id="resenas" sx={secSx('resenas')}>
          <Container maxW="4xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('resenas')}</ElegantHeading></ScrollReveal>
            {(avales.length > 0 || (bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0)) ? (
            <>
            {avales.length > 0 && (
              <ScrollRevealSoft delay={0.08}>
                <MicrositeAvalesBlock
                  avales={avales}
                  variant="elegante"
                  surfaceColor={surfaceColor}
                  mb={bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0 ? 12 : 0}
                />
              </ScrollRevealSoft>
            )}
            {bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0 && (
              <ScrollRevealSoft delay={avales.length > 0 ? 0.15 : 0.08}>
                <MicrositeResenasBlock
                  resenas={bandaData.resenasRecibidas.filter((r) => r.isPublished)}
                  stats={stats}
                  variant="elegante"
                  primaryColor={primaryColor}
                  surfaceColor={surfaceColor}
                />
              </ScrollRevealSoft>
            )}
            </>
            ) : (
              <ScrollRevealSoft delay={0.08}>
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.resenas}</Text>
              </ScrollRevealSoft>
            )}
          </Container>
        </ElegantSection>
      )}

      {/* CALENDARIO */}
      {!secHidden('calendario') && (
        <ElegantSection id="calendario" sx={secSx('calendario')}>
          <Container maxW="3xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('calendario')}</ElegantHeading></ScrollReveal>
            {(((msConfig.calendarioMostrarActuaciones !== false) && calActsVis.length > 0) || ((msConfig.calendarioMostrarEnsayos !== false) && calEnsVis.length > 0)) ? (
              <ScrollRevealSoft delay={0.08}>
                <MicrositeCalendario
                  msConfig={msConfig}
                  actuaciones={(msConfig.calendarioMostrarActuaciones !== false) ? calActsVis : []}
                  ensayos={(msConfig.calendarioMostrarEnsayos !== false) ? calEnsVis : []}
                  light={false}
                />
              </ScrollRevealSoft>
            ) : (
              <ScrollRevealSoft delay={0.08}>
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.calendario}</Text>
              </ScrollRevealSoft>
            )}
          </Container>
        </ElegantSection>
      )}

      {/* PATROCINADORES */}
      {!secHidden('patrocinadores') && (
        <ElegantSection id="patrocinadores" sx={secSx('patrocinadores')}>
          <Container maxW="4xl">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('patrocinadores')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.patrocinadores?.length > 0) ? (
                <Flex wrap="wrap" justify="center" gap={10}>
                  {msConfig.patrocinadores.map((p, i) => {
                    const content = p.logo ? <Image src={p.logo} alt={p.nombre} h="45px" w="auto" maxW="140px" objectFit="contain" opacity={0.5} _hover={{ opacity: 1 }} transition="opacity 0.3s" /> : <Text fontSize="sm" color="gray.500" fontWeight="400">{p.nombre}</Text>;
                    return (
                      <VStack key={i} gap={1}>
                        {p.url ? <Link
                          href={p.url.startsWith('http') ? p.url : `https://${p.url}`}
                          target='_blank'
                          rel='noopener noreferrer'>{content}</Link> : content}
                      </VStack>
                    );
                  })}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.patrocinadores}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {!secHidden('enlaces') && (
        <ElegantSection id="enlaces" sx={secSx('enlaces')}>
          <Container maxW="3xl" textAlign="center">
            <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('enlaces')}</ElegantHeading></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {enlacesItems.length > 0 ? (
                <VStack gap={3} align="center">
                  {enlacesItems.map((e, i) => {
                    const LinkIcon = linkIconMap[e.icono] || FaLink;
                    return (
                      <Link
                        key={i}
                        href={e.url?.startsWith('http') ? e.url : `https://${e.url || ''}`}
                        target='_blank'
                        rel='noopener noreferrer'>
                        <HStack gap={2} justify="center"><Icon color="gray.500" boxSize={4} asChild><LinkIcon /></Icon><Text fontSize="sm" color="gray.600">{e.titulo || e.url}</Text></HStack>
                      </Link>
                    );
                  })}
                </VStack>
              ) : (
                <Text fontSize="md" color="gray.500">{MICROSITE_SECTION_EMPTY_HINT.enlaces}</Text>
              )}
            </ScrollRevealSoft>
          </Container>
        </ElegantSection>
      )}

      {/* CONTACTO */}
      {!secHidden('contacto') && (
      <ElegantSection id="contacto" noBorder py={{ base: 16, md: 24 }} sx={secSx('contacto')}>
        <Container maxW="3xl" textAlign="center">
          <ScrollReveal><ElegantHeading fontFamily={headingFont}>{getMicrositeSectionLabel('contacto')}</ElegantHeading></ScrollReveal>
          {msConfig.textoContratar && cm.textoContratar && (
            <ScrollRevealSoft delay={0.08}>
              <Text color="gray.500" fontSize={{ base: 'md', md: 'lg' }} lineHeight="2" mb={10} fontWeight="300">{msConfig.textoContratar}</Text>
            </ScrollRevealSoft>
          )}
          <ScrollRevealSoft delay={msConfig.textoContratar && cm.textoContratar ? 0.14 : 0.08}>
            <VStack gap={4} align="center">
              {contacto.email && cm.email && <Link href={`mailto:${contacto.email}`} _hover={{ textDecoration: 'none' }}><Text fontSize="lg" color="gray.700" fontWeight="300" _hover={{ color: 'gray.900' }} transition="color 0.2s" letterSpacing="0.02em">{contacto.email}</Text></Link>}
              {contacto.telefono && cm.telefono && <Link href={`tel:${contacto.telefono}`} _hover={{ textDecoration: 'none' }}><Text fontSize="lg" color="gray.700" fontWeight="300" letterSpacing="0.02em">{contacto.telefono}</Text></Link>}
              {contacto.website && cm.website && <Link
                href={contacto.website.startsWith('http') ? contacto.website : `https://${contacto.website}`}
                target='_blank'
                rel='noopener noreferrer'><Text fontSize="md" color="gray.500" fontWeight="300" letterSpacing="0.02em">{contacto.website}</Text></Link>}
              {locationText && cm.ubicacion && <Text fontSize="md" color="gray.500" fontWeight="300" letterSpacing="0.02em">{locationText}</Text>}
            </VStack>
          </ScrollRevealSoft>
          {hasSocials && cm.redes && (
            <ScrollRevealSoft delay={msConfig.textoContratar && cm.textoContratar ? 0.2 : 0.14}>
              <HStack gap={6} justify="center" mt={8}>
                {social.instagram && <Link
                  href={getSocialUrl('instagram', social.instagram)}
                  target='_blank'
                  rel='noopener noreferrer'><Icon
                  boxSize={5}
                  color="gray.400"
                  _hover={{ color: 'gray.700' }}
                  transition="color 0.3s"
                  asChild><FaInstagram /></Icon></Link>}
                {social.facebook && <Link
                  href={getSocialUrl('facebook', social.facebook)}
                  target='_blank'
                  rel='noopener noreferrer'><Icon
                  boxSize={5}
                  color="gray.400"
                  _hover={{ color: 'gray.700' }}
                  transition="color 0.3s"
                  asChild><FaFacebook /></Icon></Link>}
                {social.youtube && <Link
                  href={getSocialUrl('youtube', social.youtube)}
                  target='_blank'
                  rel='noopener noreferrer'><Icon
                  boxSize={5}
                  color="gray.400"
                  _hover={{ color: 'gray.700' }}
                  transition="color 0.3s"
                  asChild><FaYoutube /></Icon></Link>}
{social.twitter && <Link
                  href={getSocialUrl('twitter', social.twitter)}
                  target='_blank'
                  rel='noopener noreferrer'><Icon
                  boxSize={5}
                  color="gray.400"
                  _hover={{ color: 'gray.700' }}
                  transition="color 0.3s"
                  asChild><FaXTwitter /></Icon></Link>}
              </HStack>
            </ScrollRevealSoft>
          )}
          {contactoBlockEmpty && (
            <ScrollRevealSoft delay={0.08}>
              <Text fontSize="md" color="gray.500" mt={6}>{MICROSITE_SECTION_EMPTY_HINT.contacto}</Text>
            </ScrollRevealSoft>
          )}
        </Container>
      </ElegantSection>
      )}
      </Flex>

      {/* Image lightbox */}
      <Dialog.Root open={!!selectedImage} size='full' placement='center' onOpenChange={e => {
        if (!e.open) {
          setSelectedImage(null);
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.900" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" boxShadow="none">
              <Dialog.CloseTrigger color="white" size="lg" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.700' }} rounded="full" m={4} zIndex={10} />
              <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4} onClick={() => setSelectedImage(null)}>
                {selectedImage && <Image src={selectedImage} alt="Imagen" maxH="90vh" maxW="90vw" objectFit="contain" onClick={(e) => e.stopPropagation()} />}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </MicrositeLayout>
  );
};

export default MicrositeElegante;
